import React from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Message } from './Message';
import type { UIMessage, ChatStatus } from '../../types/chat';
import { useAgent } from '../../context/AgentContext';

interface MessageListProps {
  messages: UIMessage[];
  status: ChatStatus;
  /** Last send/stream failure (e.g. HTTP status text) — shown under “Failed to send”. */
  sendErrorDetail?: string | null;
  onRetry?: () => void;
  onRegenerate?: () => void;
  userId?: string;
  toolMetadata?: Map<string, {
    startedAt?: number;
    completedAt?: number;
    durationMs?: number;
    category?: string;
    serverName?: string;
    parentToolCallId?: string;
  }>;
  /** Whether the pre-first-message welcome greeting is still being fetched */
  welcomeMessageLoading?: boolean;
}

function nameToGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash * 31) + name.charCodeAt(i)) >>> 0;
  }
  const gradients = [
    'from-violet-500 to-purple-600',
    'from-emerald-500 to-teal-600',
    'from-sky-500 to-blue-600',
    'from-amber-500 to-orange-500',
    'from-rose-500 to-pink-600',
    'from-indigo-500 to-violet-600',
    'from-cyan-500 to-sky-600',
    'from-fuchsia-500 to-purple-600',
  ];
  return gradients[hash % gradients.length];
}

function toInitials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function EmptyState({
  agentName,
  agentDescription,
  welcomeMessageLoading,
}: {
  agentName: string;
  agentDescription?: string;
  welcomeMessageLoading?: boolean;
}) {
  const gradient = nameToGradient(agentName);
  const initials = toInitials(agentName);
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-4">
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-6 shadow-lg`}>
        <span className="text-xl font-bold text-white tracking-wide select-none">{initials}</span>
      </div>
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">
        Chat with {agentName}
      </h3>
      {welcomeMessageLoading && (
        <div className="flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 mb-6">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          <span>Loading greeting…</span>
        </div>
      )}
      {!welcomeMessageLoading && agentDescription && (
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
          {agentDescription}
        </p>
      )}
      {!welcomeMessageLoading && !agentDescription && (
        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
          Ask me anything! I'm here to help with questions, tasks, and creative projects.
        </p>
      )}
      <div className="flex flex-wrap justify-center gap-2">
        {['Ask a question', 'Get help with code', 'Brainstorm ideas'].map((suggestion) => (
          <span
            key={suggestion}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-medium"
          >
            <Sparkles className="w-3 h-3 text-amber-400" />
            {suggestion}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Hide messages that are purely system hints injected by PlanNotebook. */
function isSystemHint(msg: UIMessage): boolean {
  if (msg.role !== 'user') return false;
  const text = msg.parts
    .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
    .map((p) => p.text)
    .join('')
    .trim();
  return text.startsWith('<system-hint>') && text.endsWith('</system-hint>');
}

export const MessageList = React.memo(function MessageList({
  messages,
  status,
  sendErrorDetail,
  onRetry,
  onRegenerate,
  userId,
  toolMetadata,
  welcomeMessageLoading,
}: MessageListProps) {
  const { selectedAgent } = useAgent();
  const isStreaming = status === 'streaming';
  const isSubmitted = status === 'submitted';

  const visibleMessages = messages.filter((m) => !isSystemHint(m));
  const lastMessageIndex = visibleMessages.length - 1;

  if (visibleMessages.length === 0) {
    return (
      <EmptyState
        agentName={selectedAgent?.name ?? 'the agent'}
        agentDescription={selectedAgent?.description}
        welcomeMessageLoading={welcomeMessageLoading}
      />
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <div className="space-y-6">
        {visibleMessages.map((message, index) => {
          const isLastMessage = index === lastMessageIndex;
          const isAssistantMessage = message.role === 'assistant';
          const messageIsStreaming =
            isLastMessage && isAssistantMessage && (isStreaming || isSubmitted);
          const isLastAssistantMessage =
            isAssistantMessage &&
            visibleMessages.slice(index + 1).every((m) => m.role !== 'assistant');

          return (
            <Message
              key={message.id}
              message={message}
              isStreaming={messageIsStreaming}
              status={isLastMessage && isAssistantMessage ? status : undefined}
              sendErrorDetail={
                isLastMessage && status === 'error' ? sendErrorDetail : undefined
              }
              onRetry={isLastMessage && status === 'error' ? onRetry : undefined}
              onRegenerate={isLastAssistantMessage ? onRegenerate : undefined}
              isLastAssistantMessage={isLastAssistantMessage}
              userId={userId}
              toolMetadata={toolMetadata}
            />
          );
        })}
      </div>
    </div>
  );
});
